import { lastMonthRange, lastQuarterRange } from './periods';

/**
 * Build period options for every period chooser (plans 2195, 2209):
 *   - Last Month (LM) and Last Quarter (LQ), previous calendar month / quarter (utils/periods.js)
 *   - Rolling multi-quarter ranges (last 4/8/12/16 quarters)
 *   - Full-year ranges (FY YYYY or FY YYYY YTD for the current year)
 *   - Individual quarters (Q1–Q4)
 * Within each year, full-year comes first, then quarters most-recent-first.
 * Years are ordered most-recent-first overall.
 * Returns { label, value, startDate, endDate } objects.
 */
export function buildQuarterOptions() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);

  const pad = (n) => String(n).padStart(2, '0');
  const lastDayOf = (year, month) => new Date(year, month, 0).getDate();

  const options = [];

  // Rolling multi-quarter periods (end = last day of current quarter)
  const endMonth = currentQuarter * 3;
  const rollingEndDate = `${currentYear}-${pad(endMonth)}-${pad(lastDayOf(currentYear, endMonth))}`;
  // Absolute 0-indexed quarter count: (year-2000)*4 + (q-1)
  const currentTotalQ = (currentYear - 2000) * 4 + (currentQuarter - 1);

  options.push({ label: 'Last Month', value: 'LM', ...lastMonthRange(now) });
  options.push({ label: 'Last Quarter', value: 'LQ', ...lastQuarterRange(now) });

  [4, 8, 12, 16].forEach(n => {
    const startTotalQ = currentTotalQ - (n - 1);
    const startYear = 2000 + Math.floor(startTotalQ / 4);
    const startQ = (startTotalQ % 4) + 1;
    const startMonth = (startQ - 1) * 3 + 1;
    options.push({
      label: `Last ${n} Quarters`,
      value: `L${n}Q`,
      startDate: `${startYear}-${pad(startMonth)}-01`,
      endDate: rollingEndDate,
    });
  });

  for (let year = currentYear; year >= 2025; year--) {
    const maxQ = year === currentYear ? currentQuarter : 4;
    const ytd = year === currentYear && maxQ < 4;

    // Full-year / YTD entry
    const fyEndMonth = maxQ * 3;
    options.push({
      label: ytd ? `FY ${year} YTD` : `FY ${year}`,
      value: `FY-${year}`,
      startDate: `${year}-01-01`,
      endDate: `${year}-${pad(fyEndMonth)}-${pad(lastDayOf(year, fyEndMonth))}`,
    });

    // Individual quarters — most recent first within the year
    for (let q = maxQ; q >= 1; q--) {
      const startMonth = (q - 1) * 3 + 1;
      const endMonth = q * 3;
      options.push({
        label: `Q${q} ${year}`,
        value: `Q${q}-${year}`,
        startDate: `${year}-${pad(startMonth)}-01`,
        endDate: `${year}-${pad(endMonth)}-${pad(lastDayOf(year, endMonth))}`,
      });
    }
  }

  options.push({ label: 'Custom', value: 'custom', startDate: null, endDate: null });
  return options;
}

// The page default is Last 4 Quarters whatever else leads the list.
export const defaultPeriodValue = (options) => (options.find(o => o.value === 'L4Q') || options[0])?.value;

// Rolling periods (LM, LQ, L4Q..) as opposed to FY / individual quarters / custom.
export const isRollingPeriod = (value) => /^L(M|Q|\d+Q)$/.test(value || '');
